<template>
  <footer
    ref="conversationFooter"
    class="octadesk-chat-sends"
    :class="{ disabled: getUploadInProcess }"
  >
    <EmojiModal @finishSelection="desktopFocus()" />

    <div
      v-if="conversationClosed && !creatingChat"
      class="octadesk-chat-new-conversation"
    >
      <button
        :style="`background-color: rgb(${settings.bgColorRGB}); color: rgba(${settings.fontColorRGBA});`"
        type="submit"
        class="octadesk-button octadesk-button__new_chat"
        @click="handleCreateNewChat()"
      >
        {{ $t('conversation.footer.startNewConversation') }}
        <svg viewBox="0 0 18 18" width="18" height="18">
          <use xlink:href="#octadesk-app-icon-send" />
        </svg>
      </button>
    </div>

    <div
      v-if="!conversationClosed && !creatingChat"
      class="octadesk-chat-textarea"
    >
      <div class="animation-pose-container">
        <ConversationQuote
          v-if="unsendedQuoteMessage && !isVoiceRecording"
          :quote="unsendedQuoteMessage"
          class="conversation-on-footer"
        />
      </div>

      <div
        class="octadesk-chat-textarea-elements"
        :class="{ 'flex-start-align': commentRows >= 3 }"
      >
        <ConversationFooterComment
          ref="footerComment"
          :get-footer-height="getFooterHeight"
          :sending="sending"
          :handle-send-message="handleSendMessage"
          @expand="handleFooterCommentExpand"
        />

        <ConversationFooterActions
          :get-footer-height="getFooterHeight"
          :handle-send-message="handleSendMessage"
          :sending="sending"
        />
      </div>
    </div>
  </footer>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'

  import { roomStatus } from '@/helpers'

  import ConversationQuote from './ConversationQuote'

  import ConversationFooterActions from './ConversationFooterActions'

  import ConversationFooterComment from './ConversationFooterComment.vue'

  import EmojiModal from '@/app/shared-components/EmojiModal'

  import services from '@/services'

  export default {
    components: {
      ConversationQuote,
      ConversationFooterActions,
      ConversationFooterComment,
      EmojiModal
    },
    data() {
      return {
        sending: false,
        activityTimeout: undefined,
        commentRows: 1
      }
    },
    computed: {
      ...mapGetters([
        'settings',
        'chat',
        'conversationClosed',
        'restoringSession',
        'creatingChat',
        'forceSelectFields',
        'emojiPageStatus',
        'unsendedQuoteMessage',
        'comment',
        'showKbCard',
        'isVoiceRecording',
        'showNumberInput',
        'showDatePicker',
        'externalLogin',
        'kbEnabled',
        'getUploadInProcess'
      ]),
      messages() {
        return this.chat ? this.chat.messages : []
      },
      conversationStarted() {
        return (
          this.chat &&
          (this.chat.status === roomStatus.waiting ||
            this.chat.status === roomStatus.talking)
        )
      }
    },
    watch: {
      showKbCard(status) {
        if (!status) {
          setTimeout(() => {
            this.getFooterHeight()
          }, 0)
        }
      },
      creatingChat() {
        this.getFooterHeight()
      },
      unsendedQuoteMessage(quoteMessage) {
        if (quoteMessage) {
          this.desktopFocus()
        }
      },
      emojiPageStatus(status) {
        setTimeout(() => {
          this.setOverlayOpen(status)
        }, 0)
      },
      conversationClosed() {
        this.getFooterHeight()
      }
    },
    mounted() {
      this.updateActivity()

      this.getFooterHeight()

      this.$emit('dropZoneReady')

      this.subscribeWindowResize()

      const emojiSpriteImage = new Image()

      emojiSpriteImage.src =
        'https://unpkg.com/emoji-datasource-apple@4.0.4/img/apple/sheets-256/64.png'
    },
    beforeDestroy() {
      this.unsubscribeWindowResize()
    },
    methods: {
      ...mapActions([
        'sendMessage',
        'createChat',
        'newConversation',
        'updateActivity',
        'chooseFlux',
        'disableEmojiPage',
        'cancelQuote',
        'updateConversationFooterHeight',
        'updateComment',
        'setOverlayOpen',
        'setShowNumberInput',
        'setShowDatePicker',
        'setShowKbCard',
        'setIsSurve',
        'setSurveyFinished'
      ]),
      handleCreateNewChat() {
        this.newConversation()
        this.updateActivity()
        this.setSurveyFinished(false)

        services.iframe.postMessage({
          name: 'analytics',
          type: 'woz test widget start new conversation in widget'
        })

        if (this.externalLogin && !this.forceSelectFields) {
          this.chooseFlux().then(() => {
            this.createChat()

            if (this.kbEnabled) {
              this.setShowKbCard(true)
            }
          })
        } else {
          this.$router.push('/login')
        }
      },
      desktopFocus() {
        this.$nextTick(() => {
          this.$refs.footerComment.$refs.comment &&
            this.$refs.footerComment.$refs.comment.focus()
        })
      },
      onSendMessage() {
        this.updateComment('')

        this.cancelQuote()

        this.sending = false

        this.desktopFocus()

        this.getFooterHeight()

        this.$nextTick(() => {
          if (this.$refs && this.$refs.footerComment) {
            this.$refs.footerComment.autoExpandTextarea()
          }
        })
      },
      trySendMessage() {
        if (this.chat) {
          this.sendMessage({
            comment: this.comment,
            quotedMessage: this.unsendedQuoteMessage
          })

          if (this.showNumberInput) {
            this.setShowNumberInput(false).then(() => {
              this.onSendMessage()
            })
          } else if (this.showDatePicker) {
            this.setShowDatePicker(false).then(() => {
              this.onSendMessage()
            })
          } else {
            this.onSendMessage()
          }
        } else {
          this.startConversation()
        }
      },
      handleSendMessage(event) {
        if (!(event && event.shiftKey)) {
          if (event) {
            event.preventDefault()
          }

          this.sending = true

          this.trySendMessage()

          this.disableEmojiPage()

          services.iframe.postMessage({
            name: 'analytics',
            type: 'woz test widget send message'
          })
        }
      },
      startConversation() {
        if (!this.restoringSession && !this.chat) {
          this.createChat().then(() => {
            if (this.comment && this.comment.length) {
              this.sendMessage(this.comment)

              this.onSendMessage()
            }
          })
        }
      },
      getFooterHeight() {
        const timer = setTimeout(() => {
          if (this.$refs.conversationFooter) {
            const styles = window.getComputedStyle(
              this.$refs.conversationFooter
            )

            const margin =
              parseFloat(styles['marginTop'].replace('px', '')) +
              parseFloat(styles['marginBottom'].replace('px', ''))

            const bottom = parseFloat(styles['bottom'].replace('px', ''))

            const footerHeight = Math.ceil(
              this.$refs.conversationFooter.offsetHeight + margin + bottom
            )

            this.updateConversationFooterHeight(footerHeight)
          }

          clearTimeout(timer)
        }, 0)
      },
      subscribeWindowResize() {
        this.$nextTick(function () {
          window.addEventListener('resize', this.getFooterHeight)
        })
      },
      unsubscribeWindowResize() {
        window.removeEventListener('resize', this.getFooterHeight)
      },
      handleFooterCommentExpand(rows) {
        this.commentRows = rows
      }
    }
  }
</script>

<style>
  .octadesk-chat-sends {
    position: absolute;
    display: block;
    width: 100%;
    min-height: 60px;
    right: 0;
    bottom: 0px;
    left: 0;
    padding: 8px 16px;
    background: #fff;
    border-top: 1px solid #c3c3c3;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0px 6px 6px 0px rgba(0, 0, 0, 0.15);
  }

  .octadesk-chat-sends.disabled {
    pointer-events: none;
    cursor: not-allowed;
    background-color: #fafafa;
  }

  .octadesk-chat-textarea {
    width: 100%;
    background: transparent;
    border-radius: 12px;
  }

  .octadesk-app > .popover > .popover-content .btn {
    border-radius: 5px;
    vertical-align: top;
    float: right;
  }

  .octadesk-app > .popover > .popover-title {
    text-align: center !important;
    font-size: 14px !important;
    padding-bottom: 0 !important;
  }

  .octadesk-app > .popover {
    background: #ffffff !important;
    border: none !important;
  }

  .octadesk-app > .popover.left > .arrow {
    border-left-color: #f0daa6 !important;
  }

  .octadesk-app > .popover.left > .arrow:after {
    border-left-color: #fff7e0 !important;
  }

  /* Popover bottom */
  .octadesk-app > .popover.bottom {
    left: auto;
    right: 10px !important;
  }

  .octadesk-app > .popover.bottom > .arrow {
    left: auto;
    right: 15px;
  }

  .octadesk-app > .popover.bottom > .arrow:after {
    border-bottom-color: #ffffff !important;
  }

  .octadesk-button__new_chat {
    padding: 0.6rem 2rem 0.6rem 1rem;
    margin-top: 1rem;
  }

  .octadesk-button__new_chat svg {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-40%);
    text-align: left;
  }

  @media screen and (max-width: 350px) {
    .octadesk-chat-textarea-wrapper {
      padding: 9px 9px 6px 9px !important;
    }
  }

  .octadesk-chat-textarea-elements {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .footer-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 0;
    cursor: pointer;
    outline: none;
    padding-top: 4px;
    padding-left: 2px;
    margin-right: 9px;
    opacity: 1;
    color: var(--button-active-color);
    background: var(--button-active-bg);
    transition: background-color, 0.6s;
    position: relative;
    z-index: 1;
  }

  .footer-btn:disabled {
    background-color: #dbdbdb;
    color: #666;
    opacity: 0.6;
  }

  .flex-start-align {
    align-items: flex-start;
  }

  .animation-pose-container {
    position: fixed;
    width: 92%;
    z-index: 1;
  }
</style>
